/**
 * Contact us page content. Sourced from the Figma design (file
 * C3RJvBLyrsUhclICNDoaGF, large breakpoint node 72:4829). Copy is taken
 * verbatim — the typo "inquires" matches the figma. Trademark symbols are
 * stripped from body copy per project rule.
 */

export interface ContactNumber {
  type: 'Tel' | 'Fax';
  value: string;
}

export interface Contact {
  name: string;
  email: { label: string; href: string };
  /**
   * Lookup key under `contact.addresses.<addressKey>` in the translation
   * files. Each entry there is a multi-line postal address rendered one
   * line per `<p>`. Each language localises only its own office's
   * address (e.g. Korean for the Korea office under `ko`); other offices
   * fall back to the English version. Optional — the General Enquiries
   * card has no postal address.
   */
  addressKey?: string;
  numbers: readonly ContactNumber[];
}

export interface ContactRegion {
  /** Heading rendered in the left column (Karla Bold 24/24 midnight). */
  region: string;
  /** Heading id — referenced by the parent section's aria-labelledby. */
  id: string;
  contacts: readonly Contact[];
}

export const CONTACT_HEADER = {
  title: 'Reach us',
  intro: [
    'Have a question about our products, or interested in partnering with Clearlab to bring our lens range to your practice?',
    'Whether you are looking to set up a direct supply account, learn more about our technology, or simply want to know which lens is right for your patients, our team is here to help. Reach out and we will get back to you as soon as possible.',
  ],
} as const;

/**
 * Strings rendered inside the contact form (figma node 72:4850). The four
 * required fields are full name, email, message, and region; subject is
 * optional. The send button stays disabled until every required field is
 * non-empty, then transitions to clearlab-blue.
 */
export const CONTACT_FORM = {
  fields: {
    fullName: { label: 'Full name', required: true },
    email: { label: 'Email address', required: true },
    subject: { label: 'Subject (optional)', required: false },
    message: { label: 'Type your message here', required: true },
  },
  region: {
    label: 'Select region',
    menuLabel: 'Region',
    required: true,
    options: [
      { value: 'americas', label: 'Americas' },
      { value: 'asia', label: 'Asia' },
      { value: 'europe', label: 'Europe' },
    ],
  },
  submitLabel: 'Send message',
} as const;

export const CONTACT_REGIONS: readonly ContactRegion[] = [
  {
    region: 'Asia',
    id: 'contact-asia-heading',
    contacts: [
      {
        name: 'Clearlab SG Pte Ltd',
        email: { label: 'enquiry@clearlab.com', href: 'mailto:enquiry@clearlab.com' },
        addressKey: 'singapore',
        numbers: [
          { type: 'Tel', value: '+65 6749 1090' },
          { type: 'Fax', value: '+65 6282 0926' },
        ],
      },
      {
        name: 'Mi Gwang Contact Lens Co., Ltd.',
        email: { label: 'enquirymg@clearlab.com', href: 'mailto:enquirymg@clearlab.com' },
        addressKey: 'korea',
        numbers: [
          { type: 'Tel', value: '+82 53 811 2262' },
          { type: 'Fax', value: '+82 53 811 7938' },
        ],
      },
    ],
  },
  {
    region: 'Europe',
    id: 'contact-europe-heading',
    contacts: [
      {
        name: 'Clearlab Spain S.L',
        email: { label: 'pedidos@clearlab.com', href: 'mailto:pedidos@clearlab.com' },
        addressKey: 'spain',
        numbers: [{ type: 'Tel', value: '+34 910 609 723' }],
      },
      {
        name: 'Clearlab GmbH',
        email: { label: 'InfoDACH@clearlab.com', href: 'mailto:InfoDACH@clearlab.com' },
        addressKey: 'germany',
        numbers: [
          { type: 'Tel', value: '+49 711 9000 7454' },
          { type: 'Fax', value: '+49 711 9000 7455' },
        ],
      },
    ],
  },
  {
    region: 'Americas',
    id: 'contact-americas-heading',
    contacts: [
      {
        name: 'Clearlab US, Inc',
        email: { label: 'EnquiryUS@clearlab.com', href: 'mailto:EnquiryUS@clearlab.com' },
        addressKey: 'us',
        numbers: [
          { type: 'Tel', value: '+1 770 271 0211' },
          { type: 'Fax', value: '+1 770 271 0225' },
        ],
      },
    ],
  },
];

/**
 * "General enquiries" section (figma node 98:917). Sits at the foot of the
 * page above the legal disclaimer; provides a fallback contact for anything
 * not covered by the regional rows.
 */
export const GENERAL_ENQUIRIES_SECTION: { title: string; intro: string; contact: Contact } = {
  title: 'General enquiries',
  intro: 'For all other enquiries, contact us at:',
  contact: {
    name: 'Clearlab SG Pte Ltd',
    email: { label: 'enquiry@clearlab.com', href: 'mailto:enquiry@clearlab.com' },
    numbers: [
      { type: 'Tel', value: '+65 6749 1090' },
      { type: 'Fax', value: '+65 6282 0926' },
    ],
  },
};

export const CONTACT_DISCLAIMER =
  'By submitting your details through this form, you consent to Clearlab International Pte. Ltd. storing and processing your information for the purpose of responding to your enquiry. Your information will not be shared with third parties. Response times may vary by region. For urgent matters, please contact your regional Clearlab representative directly.';
